import { FC, SVGProps, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import { ReactComponent as CompareIcon } from 'src/assets/svg/compare-16.svg'
import { ConfirmationModalContent } from 'src/components/ConfirmationModalContent'
import en from 'src/constants/en'
import { BaseModal } from 'src/containers/BaseModal'
import { ModuleMeta } from 'src/charts/YourReports/ReportCard/moduleMeta'
import { Report } from 'src/charts/YourReports/interfaces/report.interface'
import { ReportCard, ReportCardCopy } from 'src/charts/YourReports/ReportCard'
import { Skeleton } from 'src/components/Skeleton'
import styles from './comparisonHistoryPanel.module.scss'

// The list of comparison runs: a header with its Add button, then a row per run
// showing whether it is generating, failed or ready to download.
//
// Data Comparison (one campaign over two periods) and Campaign Comparison
// (several campaigns over one period) present the same list of the same states,
// so they share it. What differs — how a row is named, and which modal the Add
// button opens — stays with the caller: it maps its own rows to the card's shape
// and renders its own modal.
interface ComparisonHistoryPanelProps {
  addLabel: string
  // What the delete confirmation calls the thing being deleted.
  deleteTarget: string
  icon: FC<SVGProps<SVGSVGElement>>
  isLoading?: boolean
  onAdd: () => void
  onDelete: (id: string) => void
  // Saves the run's PDF. Awaited so the row can show its downloading state for
  // as long as the file is actually being fetched.
  onDownload: (id: string) => Promise<void>
  // Opens a finished run's report. Rows that are not ready have nothing to
  // show, so the panel only wires this up for those that are.
  onOpen?: (id: string) => void
  onRetry: (id: string) => void
  // What each row is a comparison of. One pill per row — a comparison is only
  // ever one kind of thing — but the two lists compare different things, so
  // each names its own.
  pillLabel: string
  rowCopy: ReportCardCopy
  rows: Report[]
  subtitle: string
  title: string
}

const SKELETON_COUNT = 3
// .compactCard is 104px / radius 18 (reportCard.module.scss).
const SKELETON_HEIGHT = 104
const SKELETON_RADIUS = 18

const pillFor = (label: string): ModuleMeta[] => [
  {
    background: '#3E97BA1A',
    color: '#3E97BA',
    icon: CompareIcon,
    label,
  },
]

export const ComparisonHistoryPanel: FC<ComparisonHistoryPanelProps> = ({
  addLabel,
  deleteTarget,
  icon,
  isLoading = false,
  onAdd,
  onDelete,
  onDownload,
  onOpen,
  onRetry,
  pillLabel,
  rowCopy,
  rows,
  subtitle,
  title,
}) => {
  const [downloadingIds, setDownloadingIds] = useState<string[]>([])
  // A comparison is minutes of work and cannot be recovered, so removing one
  // asks first — as deleting a campaign or an AI Listening campaign already
  // does. The row to delete is held rather than a boolean, so the modal knows
  // which one it is confirming.
  const [idToDelete, setIdToDelete] = useState<string | null>(null)

  const handleDownload = async (id: string) => {
    setDownloadingIds((current) => [...current, id])

    try {
      await onDownload(id)
    } finally {
      setDownloadingIds((current) => current.filter((each) => each !== id))
    }
  }

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>{title}</h3>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <button className={styles.addButton} onClick={onAdd} type="button">
          <FontAwesomeIcon
            className={styles.addButtonIcon}
            icon={faPlusCircle}
          />
          {addLabel}
        </button>
      </div>

      <span className={styles.divider} />

      <div className={styles.list}>
        {isLoading
          ? // Placeholders rather than an empty list: "you have no comparisons"
            // is a claim, and it is not known until the list has loaded.
            Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <Skeleton
                height={SKELETON_HEIGHT}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                radius={SKELETON_RADIUS}
              />
            ))
          : rows.map((row) => (
              <ReportCard
                compact
                copy={rowCopy}
                icon={icon}
                iconClassName={styles.reportIcon}
                key={row.id}
                onDelete={() => setIdToDelete(row.id)}
                onDownload={() => handleDownload(row.id)}
                onOpen={
                  onOpen && row.status === 'ready'
                    ? () => onOpen(row.id)
                    : undefined
                }
                onRetry={() => onRetry(row.id)}
                pills={pillFor(pillLabel)}
                report={
                  // `downloading` is a client-only status the card understands;
                  // the row is still `ready` on the server.
                  downloadingIds.includes(row.id)
                    ? { ...row, status: 'downloading' }
                    : row
                }
              />
            ))}
      </div>

      <BaseModal isOpen={!!idToDelete}>
        <ConfirmationModalContent
          onCancel={() => setIdToDelete(null)}
          onConfirm={() => {
            if (idToDelete) {
              onDelete(idToDelete)
              setIdToDelete(null)
            }
          }}
          title={`${en.areYouSureYouWantToDelete} ${deleteTarget}?`}
        />
      </BaseModal>
    </>
  )
}
